import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import TimeRangePicker from "../../../components/ui/TimeRangePicker";
import TooltipAttributeSettingsField from "../../../components/common/TooltipAttributeSettingsField";
import ComparisonPeriodControl from "../_shared/ComparisonPeriodControl";
import { getComparisonModeOptions } from "../_shared/comparisonPeriod";
import RollingDayPresets from "../../filter/TabFilterModal/RollingDayPresets";
import { useOcelMetadata } from "../../../stores/OcelContext";

/** Format a date string as "DD. Monat YYYY" (e.g., 06. Februar 2025). */
function formatDisplayDate(value) {
  if (!value) return "";
  const d = new Date(value + "T00:00:00");
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}

const GRANULARITY_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const ResourceUtilizationChartSettings = ({
  title,
  objectTypes,
  selectedObjectType,
  availableResources,
  configuredResources,
  shiftStart,
  shiftEnd,
  breaks,
  toleranceThreshold,
  ignoreShortTransitions,
  includeNonWorkingDays,
  includeNoDataResources,
  multiResourceMode,
  enabledGranularities,
  comparisonEnabled,
  comparisonMode,
  comparisonPeriodsCount,
  limitComparisonToCurrentProgress,
  showMovingAverage,
  comparisonDisabledReason = "",
  selectedDate,
  rollingOffset,
  resourceLastActiveDate,
  viewMode,
  tooltipAttributes,
  activeFile,
  onSelectedDateChange,
  onTitleChange,
  onObjectTypeChange,
  onTooltipAttributesChange,
  onConfiguredResourcesChange,
  onShiftStartChange,
  onShiftEndChange,
  onAddBreak,
  onRemoveBreak,
  onUpdateBreak,
  onToleranceThresholdChange,
  onIgnoreShortTransitionsChange,
  onIncludeNonWorkingDaysChange,
  onIncludeNoDataResourcesChange,
  onMultiResourceModeChange,
  onEnabledGranularitiesChange,
  onComparisonEnabledChange,
  onComparisonModeChange,
  onComparisonPeriodsCountChange,
  onLimitComparisonToCurrentProgressChange,
  onShowMovingAverageChange,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [thresholdInput, setThresholdInput] = useState(String(toleranceThreshold ?? 0));
  const comparisonModeOptions = useMemo(() => getComparisonModeOptions(t), [t]);

  // Tooltip attribute options — object attributes of selectedObjectType, the
  // production-order object type the whole downtime computation is already
  // scoped to (unlike ResourceStatusMonitor, no separate tooltip object-type
  // override is needed here).
  const metadata = useOcelMetadata(activeFile);
  const tooltipAttributeOptions = useMemo(() => {
    const scoped = (metadata?.object_types_schema || []).find((t) => t.name === selectedObjectType);
    return (scoped?.attributes || []).map((a) => a.name);
  }, [selectedObjectType, metadata]);

  // Stale tooltip columns from a different object type shouldn't silently linger.
  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    onTooltipAttributesChange([]);
  };

  const toggleGranularity = (id) => {
    if (enabledGranularities.includes(id)) {
      const next = enabledGranularities.filter((g) => g !== id);
      if (next.length > 0) {
        onEnabledGranularitiesChange(next);
      }
      return;
    }
    onEnabledGranularitiesChange([...enabledGranularities, id]);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Resource Utilization Chart"
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Object Type and Resources
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Object Type *
            </label>
            <select
              value={selectedObjectType}
              onChange={(e) => handleObjectTypeChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="">Select Object Type</option>
              {(objectTypes || []).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <MultiSelectDropdown
              label="Configured Resources *"
              options={availableResources.map(String)}
              selectedValues={configuredResources}
              onSelectionChange={onConfiguredResourcesChange}
              placeholder={
                availableResources.length === 0
                  ? "Load data first..."
                  : "Select resources..."
              }
              disabled={loading || availableResources.length === 0}
              itemType="resources"
              explicitDisplayCount={5}
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <TooltipAttributeSettingsField
          attributeOptions={tooltipAttributeOptions}
          selectedAttributes={tooltipAttributes}
          onSelectedAttributesChange={onTooltipAttributesChange}
          trigger="click"
          disabled={loading}
          showTopDivider={false}
        />
      </div>

      {viewMode === "granularity" && onSelectedDateChange && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Date (Granularity mode)
          </label>
          <p className="text-xs text-gray-500">
            Currently selected: {formatDisplayDate(selectedDate)}
          </p>
          <RollingDayPresets
            value={rollingOffset}
            onChange={onSelectedDateChange}
            lastActiveDate={resourceLastActiveDate}
          />
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <TimeRangePicker
          startTime={shiftStart}
          endTime={shiftEnd}
          onStartChange={onShiftStartChange}
          onEndChange={onShiftEndChange}
          label="Shift Schedule *"
          hint="Shift is reused from Total Downtime logic."
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Breaks
          </label>
          <button
            onClick={onAddBreak}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100"
            disabled={loading}
          >
            Add Break
          </button>
        </div>
        {breaks.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No breaks configured.</p>
        ) : (
          <div className="space-y-2">
            {breaks.map((brk, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-md p-2"
              >
                <TimeRangePicker
                  startTime={brk.start}
                  endTime={brk.end}
                  onStartChange={(val) => onUpdateBreak(idx, "start", val)}
                  onEndChange={(val) => onUpdateBreak(idx, "end", val)}
                  startLabel="From"
                  endLabel="To"
                  disabled={loading}
                  className="flex-1"
                />
                <button
                  onClick={() => onRemoveBreak(idx)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="Remove break"
                  disabled={loading}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Multi-Resource Mode
        </label>
        <select
          value={multiResourceMode}
          onChange={(e) => onMultiResourceModeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        >
          <option value="accumulated">Accumulated</option>
          <option value="concurrent">Concurrent</option>
        </select>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={ignoreShortTransitions}
            onChange={(e) => onIgnoreShortTransitionsChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Ignore short transitions
          </span>
        </label>
      </div>

      {ignoreShortTransitions && (
        <div className="ml-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transition tolerance (seconds)
          </label>
          <input
            type="number"
            value={thresholdInput}
            onChange={(e) => {
              const raw = e.target.value;
              setThresholdInput(raw);
              if (raw === '') return;
              const parsed = parseInt(raw, 10);
              if (!isNaN(parsed)) {
                onToleranceThresholdChange(Math.max(0, parsed));
              }
            }}
            onBlur={() => {
              const parsed = parseInt(thresholdInput, 10);
              const safe = isNaN(parsed) ? 0 : Math.max(0, parsed);
              setThresholdInput(String(safe));
              onToleranceThresholdChange(safe);
            }}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          />
        </div>
      )}

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNonWorkingDays}
            onChange={(e) => onIncludeNonWorkingDaysChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Include Non-Working Days
          </span>
        </label>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNoDataResources}
            onChange={(e) => onIncludeNoDataResourcesChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Count resources without data as full downtime
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          When enabled, resources with no records in the dataset are treated as 100% down
          for every analyzed day
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Enabled Granularities (View Mode)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {GRANULARITY_OPTIONS.map((option) => {
            const active = enabledGranularities.includes(option.id);
            return (
              <label
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                  active
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGranularity(option.id)}
                  className="rounded border-gray-300 focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <ComparisonPeriodControl
        enabled={comparisonEnabled}
        onEnabledChange={onComparisonEnabledChange}
        mode={comparisonMode}
        onModeChange={onComparisonModeChange}
        modeOptions={comparisonModeOptions}
        limitToCurrentProgress={limitComparisonToCurrentProgress}
        onLimitToCurrentProgressChange={onLimitComparisonToCurrentProgressChange}
        loading={loading}
        disabled={!!comparisonDisabledReason}
        disabledReason={comparisonDisabledReason}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Previous periods:</span>
          {[1, 2, 3, 4].map((n) => {
            const active = comparisonPeriodsCount === n;
            const controlDisabled = loading || !!comparisonDisabledReason;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onComparisonPeriodsCountChange(n)}
                disabled={controlDisabled}
                className={`px-3 py-1 rounded-md border text-sm font-medium ${
                  active
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                } ${controlDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {n}
              </button>
            );
          })}
        </div>
      </ComparisonPeriodControl>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Show Moving Average
          </label>
          <button
            type="button"
            role="switch"
            aria-checked={!!showMovingAverage}
            onClick={() => onShowMovingAverageChange(!showMovingAverage)}
            disabled={loading}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              showMovingAverage ? "bg-primary-600" : "bg-gray-200"
            } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                showMovingAverage ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Overlays a smoothed trend line over the current period's bars only.
        </p>
      </div>
    </div>
  );
};

export default ResourceUtilizationChartSettings;
